import type {
  AllCodebooks,
  CalendarEventCategory,
  CodebooksCodebookToAdd,
  CodebooksCodebookToEdit,
  CodebooksOrderRequestCategoryToAdd,
  CodebooksOrderRequestCategoryToEdit,
  CodebooksServiceObjectRevisionCategoryToAdd,
  CodebooksServiceObjectRevisionCategoryToEdit,
  CodebooksWorkItemsWorkItemToAdd,
  CodebooksWorkItemsWorkItemToEdit,
  CustomCodebook,
  CustomCodebookItem,
  CustomCodebookItemToAdd,
  CustomCodebookItemToEdit,
  CustomCodebookToAdd,
  CustomCodebookToEdit,
  CustomerCategory,
  CustomerType,
  Group,
  GroupToAdd,
  GroupUsersToManage,
  InterventionCategory,
  InterventionCustomState,
  MaterialCategory,
  MaterialSupplier,
  OrderCategory,
  OrderCustomState,
  OrderRequestCategory,
  OrderRequestCustomState,
  PaymentType,
  ServiceObjectCategory,
  ServiceObjectCategoryToAdd,
  ServiceObjectCategoryToEdit,
  ServiceObjectCustomState,
  ServiceObjectManufacturer,
  ServiceObjectManufacturerToAdd,
  ServiceObjectManufacturerToEdit,
  ServiceObjectRevisionCategory,
  Skill,
  SkillToAdd,
  SkillUsersToManage,
  VatOption,
  VatOptionToAdd,
  VatOptionToEdit,
  WorkItem
} from 'api/models/mainservice';
import { protectedApiClient } from 'api/support/baseApiClients';
import type { CodebookToAdd, CodebookToEdit } from 'common/models';

export default {
  // Remove this endpoint when rewrite to query completed
  getCodebooks: () => {
    return protectedApiClient.get<AllCodebooks>(`/currentCompany/codebooks`);
  },
  getCodebooksQuery: async () => {
    const result = await protectedApiClient.get<AllCodebooks>(`/currentCompany/codebooks`);
    return result.data;
  },
  addCustomerCategory: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<CustomerCategory>(`/customer/category`, values);
    return result.data;
  },
  editCustomerCategory: async (params: { categoryId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/customer/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteCustomerCategory: async (categoryId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/customer/category/${categoryId}`);
    return result.data;
  },
  addCustomerType: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<CustomerType>(`/customer/type`, values);
    return result.data;
  },
  editCustomerType: async (params: { typeId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/customer/type/${params.typeId}`,
      params.values
    );

    return result.data;
  },
  deleteCustomerType: async (typeId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/customer/type/${typeId}`);
    return result.data;
  },
  addMaterialCategory: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<MaterialCategory>(`/material/category`, values);
    return result.data;
  },
  editMaterialCategory: async (params: { categoryId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/material/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteMaterialCategory: async (categoryId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/material/category/${categoryId}`);
    return result.data;
  },
  addOrderCategory: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<OrderCategory>(`/order/category`, values);
    return result.data;
  },
  editOrderCategory: async (params: { categoryId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/order/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderCategory: async (categoryId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/order/category/${categoryId}`);
    return result.data;
  },
  addServiceObjectCategory: async (values: ServiceObjectCategoryToAdd) => {
    const result = await protectedApiClient.post<ServiceObjectCategory>(
      `/serviceObject/category`,
      values
    );
    return result.data;
  },
  editServiceObjectCategory: async (params: {
    categoryId: string;
    values: ServiceObjectCategoryToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/serviceObject/category/${params.categoryId}`,
      params.values
    );
    return result.data;
  },
  deleteServiceObjectCategory: async (categoryId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/serviceObject/category/${categoryId}`
    );
    return result.data;
  },
  addServiceObjectRevisionCategory: async (values: CodebooksServiceObjectRevisionCategoryToAdd) => {
    const result = await protectedApiClient.post<ServiceObjectRevisionCategory>(
      `/serviceObject/revision`,
      values
    );
    return result.data;
  },
  editServiceObjectRevisionCategory: async (params: {
    id: string;
    values: CodebooksServiceObjectRevisionCategoryToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/serviceObject/revision/${params.id}`,
      params.values
    );
    return result.data;
  },
  deleteServiceObjectRevisionCategory: async (id: string) => {
    const result = await protectedApiClient.delete<undefined>(`/serviceObject/revision/${id}`);
    return result.data;
  },
  addServiceObjectManufacturer: async (values: ServiceObjectManufacturerToAdd) => {
    const result = await protectedApiClient.post<ServiceObjectManufacturer>(
      `/serviceObject/manufacturer`,
      values
    );

    return result.data;
  },
  editServiceObjectManufacturer: async (params: {
    manufacturerId: string;
    values: ServiceObjectManufacturerToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/serviceObject/manufacturer/${params.manufacturerId}`,
      params.values
    );

    return result.data;
  },
  deleteServiceObjectManufacturer: async (manufacturerId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/serviceObject/manufacturer/${manufacturerId}`
    );

    return result.data;
  },
  addWorkItem: async (values: CodebooksWorkItemsWorkItemToAdd) => {
    const result = await protectedApiClient.post<WorkItem>(`/workItem`, values);
    return result.data;
  },
  editWorkItem: async (params: {
    workItemId: string;
    values: CodebooksWorkItemsWorkItemToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/workItem/${params.workItemId}`,
      params.values
    );
    return result.data;
  },
  deleteWorkItem: async (workItemId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/workItem/${workItemId}`);
    return result.data;
  },
  getWorkItems: () => {
    return protectedApiClient.get<WorkItem[]>(`/workItem`);
  },
  addMaterialSupplier: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<MaterialSupplier>(`/material/supplier`, values);
    return result.data;
  },
  editMaterialSupplier: async (params: { supplierId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/material/supplier/${params.supplierId}`,
      params.values
    );
    return result.data;
  },
  deleteMaterialSupplier: async (supplierId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/material/supplier/${supplierId}`);
    return result.data;
  },
  addVatOption: async (values: VatOptionToAdd) => {
    const result = await protectedApiClient.post<VatOption>(`/vatOption`, values);
    return result.data;
  },
  editVatOption: async (params: { vatOptionId: string; values: VatOptionToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/vatOption/${params.vatOptionId}`,
      params.values
    );
    return result.data;
  },
  deleteVatOption: async (vatOptionId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/vatOption/${vatOptionId}`);
    return result.data;
  },
  addCustomCodebookItemAutoComplete: async (params: {
    slug: string;
    values: CustomCodebookItemToAdd;
  }) => {
    const result = await protectedApiClient.post<CustomCodebookItem>(
      `/customCodebook/slug/${params.slug}/item`,
      params.values
    );
    return result.data;
  },
  addCustomCodebookItem: async (params: { id: string; values: CustomCodebookItemToAdd }) => {
    const result = await protectedApiClient.post<CustomCodebookItem>(
      `/customCodebook/${params.id}/item`,
      params.values
    );
    return result.data;
  },
  editCustomCodebookItem: async (params: {
    customCodebookId: string;
    itemId: string;
    values: CustomCodebookItemToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/customCodebook/${params.customCodebookId}/item/${params.itemId}`,
      params.values
    );
    return result.data;
  },
  deleteCustomCodebookItem: async (params: { customCodebookId: string; itemId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/customCodebook/${params.customCodebookId}/item/${params.itemId}`
    );
    return result.data;
  },
  addCustomCodebook: async (values: CustomCodebookToAdd) => {
    const result = await protectedApiClient.post<CustomCodebook>(`/customCodebook`, values);
    return result.data;
  },
  editCustomCodebook: async (params: {
    customCodebookId: string;
    values: CustomCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/customCodebook/${params.customCodebookId}`,
      params.values
    );
    return result.data;
  },
  deleteCustomCodebook: async (customCodebookId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/customCodebook/${customCodebookId}`
    );
    return result.data;
  },
  addOrderCustomState: async (values: CodebookToAdd) => {
    const result = await protectedApiClient.post<OrderCustomState>(`/order/customState`, values);
    return result.data;
  },
  editOrderCustomState: async (params: { orderCustomStateId: string; values: CodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/order/customState/${params.orderCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderCustomState: async (orderCustomStateId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/order/customState/${orderCustomStateId}`
    );
    return result.data;
  },
  addServiceObjectCustomState: async (values: CodebookToAdd) => {
    const result = await protectedApiClient.post<ServiceObjectCustomState>(
      `/serviceObject/customState`,
      values
    );
    return result.data;
  },
  editServiceObjectCustomState: async (params: {
    serviceObjectCustomStateId: string;
    values: CodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/serviceObject/customState/${params.serviceObjectCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteServiceObjectCustomState: async (serviceObjectCustomStateId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/serviceObject/customState/${serviceObjectCustomStateId}`
    );
    return result.data;
  },
  addOrderRequestCustomState: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<OrderRequestCustomState>(
      `/orderRequest/customState`,
      values
    );
    return result.data;
  },
  editOrderRequestCustomState: async (params: {
    orderRequestCustomStateId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/orderRequest/customState/${params.orderRequestCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderRequestCustomState: async (orderRequestCustomStateId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/orderRequest/customState/${orderRequestCustomStateId}`
    );
    return result.data;
  },
  addGroup: async (values: GroupToAdd) => {
    const result = await protectedApiClient.post<Group>(`/group`, values);

    return result.data;
  },
  editGroup: async (params: { groupId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/group/${params.groupId}`,
      params.values
    );

    return result.data;
  },
  deleteGroup: async (groupId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/group/${groupId}`);

    return result.data;
  },
  manageGroupUsers: async (params: { groupId: string; values: GroupUsersToManage }) => {
    const result = await protectedApiClient.put<undefined>(
      `/group/${params.groupId}/manageUsers`,
      params.values
    );

    return result.data;
  },
  addSkill: async (values: SkillToAdd) => {
    const result = await protectedApiClient.post<Skill>(`/skill`, values);

    return result.data;
  },
  editSkill: async (params: { skillId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/skill/${params.skillId}`,
      params.values
    );

    return result.data;
  },
  deleteSkill: async (skillId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/skill/${skillId}`);

    return result.data;
  },
  manageSkillUsers: async (params: { skillId: string; values: SkillUsersToManage }) => {
    const result = await protectedApiClient.put<undefined>(
      `/skill/${params.skillId}/manageUsers`,
      params.values
    );

    return result.data;
  },
  addInterventionCategory: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<InterventionCategory>(
      `/order/intervention/category`,
      values
    );
    return result.data;
  },
  editInterventionCategory: async (params: {
    interventionCategoryId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/order/intervention/category/${params.interventionCategoryId}`,
      params.values
    );
    return result.data;
  },
  deleteInterventionCategory: async (interventionCategoryId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/order/intervention/category/${interventionCategoryId}`
    );
    return result.data;
  },
  addInterventionCustomState: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<InterventionCustomState>(
      `/intervention/customState`,
      values
    );
    return result.data;
  },
  editInterventionCustomState: async (params: {
    interventionCustomStateId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/intervention/customState/${params.interventionCustomStateId}`,
      params.values
    );
    return result.data;
  },
  deleteInterventionCustomState: async (interventionCustomStateId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/intervention/customState/${interventionCustomStateId}`
    );
    return result.data;
  },
  addPaymentType: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<PaymentType>(`/paymentType`, values);
    return result.data;
  },
  editPaymentType: async (params: { paymentTypeId: string; values: CodebooksCodebookToEdit }) => {
    const result = await protectedApiClient.put<undefined>(
      `/paymentType/${params.paymentTypeId}`,
      params.values
    );
    return result.data;
  },
  deletePaymentType: async (paymentTypeId: string) => {
    const result = await protectedApiClient.delete<undefined>(`/paymentType/${paymentTypeId}`);
    return result.data;
  },
  addCalendarEventCategory: async (values: CodebooksCodebookToAdd) => {
    const result = await protectedApiClient.post<CalendarEventCategory>(
      `/calendarEvent/category`,
      values
    );
    return result.data;
  },
  editCalendarEventCategory: async (params: {
    calendarEventCategoryId: string;
    values: CodebooksCodebookToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/calendarEvent/category/${params.calendarEventCategoryId}`,
      params.values
    );
    return result.data;
  },
  deleteCalendarEventCategory: async (calendarEventCategoryId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/calendarEvent/category/${calendarEventCategoryId}`
    );
    return result.data;
  },
  addOrderRequestCategory: async (values: CodebooksOrderRequestCategoryToAdd) => {
    const result = await protectedApiClient.post<OrderRequestCategory>(
      `/orderRequest/category`,
      values
    );
    return result.data;
  },
  editOrderRequestCategory: async (params: {
    orderRequestCategoryId: string;
    values: CodebooksOrderRequestCategoryToEdit;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/orderRequest/category/${params.orderRequestCategoryId}`,
      params.values
    );
    return result.data;
  },
  deleteOrderRequestCategory: async (orderRequestCategoryId: string) => {
    const result = await protectedApiClient.delete<undefined>(
      `/orderRequest/category/${orderRequestCategoryId}`
    );
    return result.data;
  }
};
